import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Moon, Pill, Salad, Activity, ChevronDown, ChevronRight, Loader2, Sparkles } from "lucide-react";
import { trackEvent } from "../../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLOUR = {
  A: "border-[#BEFF38]/40 bg-[#BEFF38]/10 text-[#BEFF38]",
  B: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  C: "border-zinc-500/30 bg-zinc-500/10 text-zinc-300",
};

const SECTIONS = [
  { key: "sleep", label: "Sleep chemistry", icon: Moon },
  { key: "supplements", label: "Supplements", icon: Pill },
  { key: "nutrients", label: "Nutrients", icon: Salad },
  { key: "hormones", label: "Hormones", icon: Activity },
];

const Row = ({ testid, title, tier, sub, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} className="rounded-xl border border-zinc-800 bg-[#0b0c10] overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-3 flex items-start gap-3"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="text-white font-bold text-sm leading-tight">{title}</p>
            {tier && (
              <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border ${TIER_COLOUR[tier] || TIER_COLOUR.C}`}>
                Tier {tier}
              </span>
            )}
          </div>
          {sub && <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{sub}</p>}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />}
      </button>
      {open && <div className="px-3 pb-3 space-y-2 text-xs text-zinc-300 leading-relaxed">{children}</div>}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{label}</p>
    <div className="mt-0.5 text-zinc-300">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="mt-0.5 space-y-0.5 list-disc list-inside">
    {items.map((it, i) => <li key={i} className="text-zinc-300">{it}</li>)}
  </ul>
);

export default function Fuel() {
  const [data, setData] = useState(null);
  const [section, setSection] = useState("sleep");
  const [loading, setLoading] = useState(true);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    trackEvent("fuel_view");
    setAck(localStorage.getItem("fff_fuel_disclaimer_ack") === "1");
    axios.get(`${BACKEND_URL}/api/fuel/`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const acknowledge = () => {
    localStorage.setItem("fff_fuel_disclaimer_ack", "1");
    setAck(true);
    trackEvent("fuel_disclaimer_ack");
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-400 text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading fuel science…</div>;
  }
  if (!data) return <p className="text-zinc-500 text-sm py-8">Couldn't load. Try again shortly.</p>;

  return (
    <div className="space-y-5">
      {/* Yō 養 hero */}
      <div data-testid="fuel-hero" className="rounded-2xl overflow-hidden border border-[#BEFF38]/15 bg-[#f7f4ee]">
        <img src="/mastery/yo.png" alt="Yō 養 — nourish, cultivate" className="w-full h-auto block" loading="lazy" />
        <div className="p-4 bg-[#0b0b0c] border-t border-white/5">
          <p className="text-[10px] uppercase tracking-[0.2em] font-black text-[#BEFF38]">Yō · 養</p>
          <p className="mt-1.5 text-white text-base font-bold leading-snug">Nourish what you're building.</p>
          <p className="mt-1 text-zinc-400 text-xs leading-relaxed">
            Training breaks you down. Sleep, food and biochemistry build you back. This is the recovery half of the equation, structured by evidence tier and gated by explicit interaction warnings.
          </p>
        </div>
      </div>

      {/* Medical disclaimer gate */}
      {!ack && (
        <div data-testid="fuel-disclaimer-gate" className="rounded-2xl border border-amber-400/40 bg-amber-400/5 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-[0.16em] font-black text-amber-300">Read before you continue</p>
              <p className="mt-1.5 text-white text-sm leading-relaxed">{data.disclaimer}</p>
              <p className="mt-2 text-zinc-400 text-xs leading-relaxed">
                Every supplement, nutrient and hormone entry below includes: dose ranges, timing, evidence tier (A / B / C), interactions with common medications, and populations it isn't appropriate for. Read all four before deciding.
              </p>
              <button
                onClick={acknowledge}
                data-testid="fuel-disclaimer-ack"
                className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#BEFF38] text-black font-black uppercase tracking-wider text-xs px-4 py-2.5 rounded-lg hover:brightness-95"
              >
                I understand — show me the content
              </button>
            </div>
          </div>
        </div>
      )}

      {ack && (
        <>
          {/* Section tabs */}
          <div className="grid grid-cols-4 gap-1.5">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = section === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => { setSection(s.key); trackEvent("fuel_section", { section: s.key }); }}
                  data-testid={`fuel-tab-${s.key}`}
                  className={`p-2.5 rounded-lg border transition-colors flex flex-col items-center gap-1 ${
                    active ? "border-[#BEFF38]/40 bg-[#BEFF38]/10 text-white" : "border-zinc-800 bg-zinc-900/40 text-zinc-500 hover:text-white"
                  }`}
                >
                  <Icon className={`w-4 h-4 ${active ? "text-[#BEFF38]" : ""}`} />
                  <span className="text-[10px] font-black uppercase tracking-wider leading-tight text-center">{s.label}</span>
                  <span className="text-[9px] tabular-nums text-zinc-500">{data.counts[s.key]}</span>
                </button>
              );
            })}
          </div>

          {/* Sleep chemistry */}
          {section === "sleep" && (
            <div className="space-y-2">
              {data.sleep.map((it) => (
                <Row key={it.id} testid={`fuel-sleep-${it.id}`} title={it.title} tier={it.evidence_tier} sub={it.summary}>
                  <Field label="Actionable">
                    <List items={it.actionable} />
                  </Field>
                </Row>
              ))}
            </div>
          )}

          {/* Supplements */}
          {section === "supplements" && (
            <div className="space-y-2">
              {data.supplements.map((it) => (
                <Row
                  key={it.id}
                  testid={`fuel-supplement-${it.id}`}
                  title={it.name}
                  tier={it.tier}
                  sub={`${it.dose} · ${it.timing}`}
                >
                  <Field label="Why">{it.why}</Field>
                  <Field label="Evidence">{it.evidence_notes}</Field>
                  {(it.interactions || []).length > 0 && <Field label="Interactions"><List items={it.interactions} /></Field>}
                  {(it.not_for || []).length > 0 && (
                    <div className="rounded-md border border-red-500/30 bg-red-500/5 p-2">
                      <p className="text-[10px] uppercase tracking-wider font-black text-red-300">Not for</p>
                      <List items={it.not_for} />
                    </div>
                  )}
                </Row>
              ))}
            </div>
          )}

          {/* Nutrients */}
          {section === "nutrients" && (
            <div className="space-y-2">
              {data.nutrients.map((it) => (
                <Row
                  key={it.id}
                  testid={`fuel-nutrient-${it.id}`}
                  title={it.name}
                  tier={it.tier}
                  sub={`Target · ${it.target}`}
                >
                  <Field label="Food first">
                    <List items={it.food_first} />
                  </Field>
                  <Field label="Why">{it.why}</Field>
                  <Field label="Evidence">{it.evidence_notes}</Field>
                </Row>
              ))}
            </div>
          )}

          {/* Hormones */}
          {section === "hormones" && (
            <div className="space-y-2">
              {data.hormones.map((it) => (
                <Row key={it.id} testid={`fuel-hormone-${it.id}`} title={it.title} sub={it.summary}>
                  {it.signals_of_chronic_elevation && (
                    <Field label="Signs of chronic elevation"><List items={it.signals_of_chronic_elevation} /></Field>
                  )}
                  {it.biggest_levers && <Field label="Biggest levers"><List items={it.biggest_levers} /></Field>}
                  {it.for_women_training && <Field label="For women training"><List items={it.for_women_training} /></Field>}
                  {it.for_men && <Field label="For men"><List items={it.for_men} /></Field>}
                  {it.actionable && <Field label="Actionable"><List items={it.actionable} /></Field>}
                </Row>
              ))}
            </div>
          )}

          {/* Persistent footer disclaimer */}
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 flex items-start gap-2">
            <Sparkles className="w-3 h-3 text-zinc-500 shrink-0 mt-0.5" />
            <p className="text-[10px] text-zinc-500 leading-relaxed">{data.disclaimer}</p>
          </div>
        </>
      )}
    </div>
  );
}
